import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { fetchCategories } from "@/utils/categoryUtils";
import { useTranslation } from "react-i18next";

interface Category {
  id: string;
  name: string;
}

interface BasicInformationProps {
  name: string;
  setName: (name: string) => void;
  category: string;
  setCategory: (category: string) => void;
  categories: Category[];
  setCategories: (categories: Category[]) => void;
  profession?: string | null;
}

export const BasicInformation = ({
  name,
  setName,
  category,
  setCategory,
  categories,
  setCategories,
  profession
}: BasicInformationProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [selectKey, setSelectKey] = useState(Date.now()); // Add a key to force re-render of Select when needed

  const getPlaceholder = () => {
    if (!profession) {
      return getCategoryBasedPlaceholder();
    }
    
    const normalizedProfession = profession.toLowerCase();
    
    if (normalizedProfession.includes("hair") || normalizedProfession.includes("stylist")) 
      return `e.g., ${t('common.serviceExamples.womensHaircut')}`;
    if (normalizedProfession.includes("nail") || normalizedProfession.includes("manicure")) 
      return `e.g., ${t('common.serviceExamples.gelManicure')}`;
    if (normalizedProfession.includes("makeup")) 
      return `e.g., ${t('common.serviceExamples.bridalMakeup')}`;
    if (normalizedProfession.includes("massage") || normalizedProfession.includes("therapist")) 
      return `e.g., ${t('common.serviceExamples.deepTissueMassage')}`;
    if (normalizedProfession.includes("photo")) 
      return `e.g., ${t('common.serviceExamples.familyPortraitSession')}`;
    if (normalizedProfession.includes("design")) 
      return `e.g., ${t('common.serviceExamples.logoDesignPackage')}`;
    if (normalizedProfession.includes("social")) 
      return `e.g., ${t('common.serviceExamples.instagramContentManagement')}`;
    if (normalizedProfession.includes("video")) 
      return `e.g., ${t('common.serviceExamples.eventVideography')}`;
    if (normalizedProfession.includes("coach")) 
      return `e.g., ${t('common.serviceExamples.careerCoachingSession')}`;
    if (normalizedProfession.includes("barber")) 
      return `e.g., ${t('common.serviceExamples.mensHaircut')}`;
    if (normalizedProfession.includes("tax") || normalizedProfession.includes("account")) 
      return `e.g., ${t('common.serviceExamples.taxReturnPreparation')}`;
    if (normalizedProfession.includes("personal") && normalizedProfession.includes("train")) 
      return `e.g., ${t('common.serviceExamples.fitnessAssessmentSession')}`;
    if (normalizedProfession.includes("mc") || normalizedProfession.includes("master of ceremonies"))
      return `e.g., ${t('common.serviceExamples.weddingMCPackage')}`;
    if (normalizedProfession.includes("moderat"))
      return `e.g., ${t('common.serviceExamples.conferencePanelModeration')}`;
    
    return `e.g., ${profession} ${t('common.serviceExamples.serviceSuffix')}`;
  };

  const getCategoryBasedPlaceholder = () => {
    if (!category) return `e.g., ${t('common.serviceExamples.fullServicePackage')}`;
    
    const selectedCategory = categories.find(cat => cat.id === category);
    if (!selectedCategory) return `e.g., ${t('common.serviceExamples.fullServicePackage')}`;
    
    const categoryName = selectedCategory.name.toLowerCase();
    
    if (categoryName.includes("hair")) return `e.g., ${t('common.serviceExamples.womensHaircut')}`;
    if (categoryName.includes("nail")) return `e.g., ${t('common.serviceExamples.gelManicure')}`;
    if (categoryName.includes("makeup")) return `e.g., ${t('common.serviceExamples.bridalMakeup')}`;
    if (categoryName.includes("massage")) return `e.g., ${t('common.serviceExamples.deepTissueMassage')}`;
    if (categoryName.includes("photo")) return `e.g., ${t('common.serviceExamples.familyPortraitSession')}`;
    if (categoryName.includes("design")) return `e.g., ${t('common.serviceExamples.logoDesignPackage')}`;
    if (categoryName.includes("social")) return `e.g., ${t('common.serviceExamples.instagramContentManagement')}`;
    if (categoryName.includes("video")) return `e.g., ${t('common.serviceExamples.eventVideography')}`;
    if (categoryName.includes("coach")) return `e.g., ${t('common.serviceExamples.careerCoachingSession')}`;
    if (categoryName.includes("tax") || categoryName.includes("account")) return `e.g., ${t('common.serviceExamples.taxReturnPreparation')}`;
    
    return `e.g., ${selectedCategory.name} ${t('common.serviceExamples.serviceSuffix')}`;
  };

  const handleCategoryChange = (value: string) => {
    if (value === "create-new") {
      setIsDialogOpen(true);
    } else {
      // Just update the category without affecting other form fields
      setCategory(value);
      console.log("Category selected:", categories.find(cat => cat.id === value)?.name);
    }
  };

  const createNewCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Category name cannot be empty",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "You must be logged in to create a category",
        });
        return;
      }

      console.log("Creating new category:", newCategoryName);
      
      const { data, error } = await supabase
        .from('service_categories')
        .insert([
          { 
            name: newCategoryName,
            user_id: user.id,
            sequence: categories.length,
            is_visible: true
          }
        ])
        .select();

      if (error) {
        console.error("Error creating category:", error);
        throw error;
      }

      console.log("Category creation response:", data);
      
      if (data && data.length > 0) {
        // Update the categories list with the new category
        const newCategory = data[0];
        setCategories([...categories, newCategory]);
        
        // Update the category selection
        setCategory(newCategory.id);
        
        // Reset local state
        setNewCategoryName("");
        setIsDialogOpen(false);
        
        // Force Select component to re-render with the new value
        setSelectKey(Date.now());
        
        toast({
          title: "Success",
          description: "Category created and selected",
        });
      }
    } catch (error) {
      console.error("Error creating category:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create category. Please try again.",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="name">{t('services.serviceNameRequired')}</Label>
        <Input
          id="name"
          placeholder={getPlaceholder()}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1"
          maxLength={50}
        />
        <div className="flex justify-end mt-1">
          <span className="text-xs text-gray-500">{name.length}/50</span>
        </div>
      </div>

      <div>
        <Label htmlFor="category">{t('services.categoryLabel')}</Label>
        <Select
          key={selectKey}
          value={category}
          onValueChange={handleCategoryChange}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={t('services.selectCategory')} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
            <SelectItem value="create-new" className="text-gebeya-pink">
              <div className="flex items-center">
                <PlusCircle className="w-4 h-4 mr-2" />
                {t('services.createNewCategory')}
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          setNewCategoryName("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('services.createNewCategoryDialog')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="categoryName">{t('services.categoryNameLabel')}</Label>
              <Input
                id="categoryName"
                placeholder={t('services.categoryNamePlaceholder')}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    createNewCategory();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={createNewCategory}
                type="button"
                className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:opacity-90"
              >
                {t('services.createAndSave')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
